import { api_configuration } from "../../common";
import api_cr = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;

type BuildStep = (tail: api_cr.Tnode_path) => api_cr.Tnode_path;

export default class NodePathBuilder {
	build_steps: BuildStep[];

	constructor(build_steps: BuildStep[] = []) {
		this.build_steps = build_steps;
	}

	copyBuildSteps(): BuildStep[] {
		return this.build_steps.slice(0);
	}

	addToBuildStepArray(element: BuildStep): BuildStep[] {
		const copy = this.copyBuildSteps();
		copy.push(element);
		return copy;
	}

	newBuilder(build_function: BuildStep): NodePathBuilder {
		return new NodePathBuilder(this.addToBuildStepArray(build_function));
	}

	clone(): NodePathBuilder {
		return new NodePathBuilder(this.copyBuildSteps());
	}

	build(): api_cr.Tnode_path {
		return this.copyBuildSteps()
			.reverse()
			.reduce<api_cr.Tnode_path>((tail, build_step) => build_step(tail), { "has steps": ["no", {}] });
	}

	parent(): NodePathBuilder {
		return this.newBuilder((tail: api_cr.Tnode_path) => {
			return {
				"has steps": [
					"yes",
					{
						type: ["parent", {}],
						tail: tail,
					},
				],
			};
		});
	}

	state_rule(rule_name: string): NodePathBuilder {
		return this.newBuilder((tail: api_cr.Tnode_path) => ({
			"has steps": [
				"yes",
				{
					type: [
						"state rule",
						{
							rule: rule_name,
						},
					],
					tail: tail,
				},
			],
		}));
	}

	reference_rule(reference_name: string, rule_name: string): NodePathBuilder {
		return this.newBuilder((tail: api_cr.Tnode_path) => ({
			"has steps": [
				"yes",
				{
					type: [
						"reference rule",
						{
							property: reference_name,
							rule: rule_name,
						},
					],
					tail: tail,
				},
			],
		}));
	}

	reference(reference_name: string): NodePathBuilder {
		return this.newBuilder((tail: api_cr.Tnode_path) => ({
			"has steps": [
				"yes",
				{
					type: [
						"reference",
						{
							text: reference_name,
						},
					],
					tail: tail,
				},
			],
		}));
	}

	group(group_name: string): NodePathBuilder {
		return this.newBuilder((tail: api_cr.Tnode_path) => ({
			"has steps": [
				"yes",
				{
					type: [
						"group",
						{
							group: group_name,
						},
					],
					tail: tail,
				},
			],
		}));
	}

	state(stategroup_name: string, state_name: string): NodePathBuilder {
		return this.newBuilder((tail: api_cr.Tnode_path) => ({
			"has steps": [
				"yes",
				{
					type: [
						"state",
						{
							"state group": stategroup_name,
							state: state_name,
						},
					],
					tail: tail,
				},
			],
		}));
	}

	collection_entry(collection_name: string, entry_key: string): NodePathBuilder {
		return this.newBuilder((tail: api_cr.Tnode_path) => ({
			"has steps": [
				"yes",
				{
					type: [
						"collection entry",
						{
							collection: collection_name,
							"entry key": entry_key,
						},
					],
					tail: tail,
				},
			],
		}));
	}
}

export function createNodePathFromObjectPathTail(path: api_app.Cobject_path_tail): NodePathBuilder {
	let node_path = new NodePathBuilder();
	let property: string | undefined;
	while (path.properties.has_steps.state.name === "yes") {
		const has_steps = path.properties.has_steps.state.node;
		switch (has_steps.properties.type.state.name) {
			case "property value": {
				const $ = has_steps.properties.type.state.node;
				property = $.properties.property.properties.property.entry;
				switch ($.properties.property.properties.property.ref.properties.type.state.name) {
					case "group": {
						node_path = node_path.group(property);
						property = undefined;
						break;
					}
					case "state group":
						break;
					case "text":
						throw new Error("unexpected step for conditional path");
					case "number":
						throw new Error("unexpected step for conditional path");
					case "file":
						throw new Error("unexpected step for conditional path");
					case "collection":
						throw new Error("unexpected step for conditional path");
					default:
						unexpectedState($.properties.property.properties.property.ref.properties.type.state);
				}
				break;
			}
			case "state":
				if (property !== undefined) {
					const state = has_steps.properties.type.state.node.properties.state.entry;
					node_path = node_path.state(property, state);
					property = undefined;
				} else {
					throw new Error("unexpected step for conditional path");
				}
				break;
			case "parent":
				throw new Error("unexpected step for conditional path");
			case "dependency":
				throw new Error("unexpected step for conditional path");
			case "reference set":
				throw new Error("unexpected step for conditional path");
			default:
				unexpectedState(has_steps.properties.type.state);
		}
		path = has_steps.properties.tail;
	}
	return node_path;
}
